/**
 * The panel's information architecture, in one place.
 *
 * The previous nav was eleven flat peers — Overview, Users, Brands, Catalog,
 * Products, Try-on, MAHI, Crawl, Flags, Audit Log, Staff — which is the
 * build order, not a structure anyone can navigate. One brand's life was
 * split across four of them, "MAHI" as a label inside MAHI's own admin panel
 * carried no information, and the owner-only Conversations page wasn't in the
 * nav at all.
 *
 * Five sections, grouped by the question being asked rather than the table
 * being queried:
 *
 *   Home     is anything wrong, and how are we doing
 *   Growth   where do users come from, and what do they do  (watch)
 *   Catalog  brands, their applications, their products     (watch + do)
 *   AI       the stylist and try-on: health, cost, quality  (watch)
 *   System   the machine itself: health, flags, audit, staff (do)
 *
 * `roles: null` means every authenticated role. Anything else is the exact
 * allow-list the backend enforces — this list only decides what gets
 * *rendered*; the API refuses regardless, per product doc Section 7 ("an
 * analyst shouldn't even see the control, not just be blocked from
 * clicking it").
 */

export const SECTIONS = [
  {
    key: 'home',
    label: 'Home',
    path: '/',
    end: true,
    roles: null,
    tabs: [],
  },
  {
    key: 'growth',
    label: 'Growth',
    path: '/growth',
    roles: null,
    tabs: [
      { key: 'overview', label: 'Overview', path: '/growth', end: true, roles: null },
      { key: 'funnel', label: 'Funnel', path: '/growth/funnel', roles: null },
      { key: 'cohorts', label: 'Cohorts', path: '/growth/cohorts', roles: null },
      { key: 'people', label: 'Find a user', path: '/growth/people', roles: ['operator', 'support', 'owner'] },
    ],
  },
  {
    key: 'catalog',
    label: 'Catalog',
    path: '/catalog',
    roles: null,
    tabs: [
      { key: 'brands', label: 'Brands', path: '/catalog', end: true, roles: null },
      { key: 'applications', label: 'Applications', path: '/catalog/applications', roles: ['operator', 'owner'], badge: 'pendingPartners' },
      { key: 'products', label: 'Products', path: '/catalog/products', roles: null },
      { key: 'crawl', label: 'Crawl', path: '/catalog/crawl', roles: null, badge: 'failedCrawls' },
    ],
  },
  {
    key: 'ai',
    label: 'AI',
    path: '/ai',
    roles: null,
    tabs: [
      { key: 'stylist', label: 'Stylist', path: '/ai', end: true, roles: null },
      { key: 'usage', label: 'LLM Usage', path: '/ai/usage', roles: null },
      { key: 'tryon', label: 'Try-on', path: '/ai/tryon', roles: null },
      { key: 'transcripts', label: 'Transcripts', path: '/ai/transcripts', roles: ['owner'] },
    ],
  },
  {
    key: 'system',
    label: 'System',
    path: '/system',
    roles: null,
    tabs: [
      { key: 'health', label: 'Health & cost', path: '/system', end: true, roles: null },
      { key: 'flags', label: 'Flags', path: '/system/flags', roles: null },
      { key: 'audit', label: 'Audit', path: '/system/audit', roles: null },
      { key: 'staff', label: 'Staff', path: '/system/staff', roles: ['owner'] },
    ],
  },
];

/** True when `role` may see this nav entry. */
export const allowed = (entry, role) => !entry.roles || entry.roles.includes(role);

export const visibleSections = (role) => SECTIONS.filter((s) => allowed(s, role));

export const tabsFor = (sectionKey, role) => {
  const section = SECTIONS.find((s) => s.key === sectionKey);
  if (!section) return [];
  return section.tabs.filter((t) => allowed(t, role));
};
